// ─────────────────────────────────────────────
// FormBuilder.jsx  (updated)
//
// QuestionStoreProvider wraps both panels.
// Neither panel receives any content as props —
// they read and write directly from the store.
// leftPanelRef is no longer needed for data flow.
// ─────────────────────────────────────────────
import { useState, useRef, useCallback, useEffect } from "react";
import { QuestionStoreProvider } from "./QuestionStore";
import LeftPanelContent from "./LeftPane/LeftPanelContent";
import RightPanel from "./RightPanel/RightPanelContent";

const MOBILE_BREAKPOINT = 768;

export default function FormBuilder({
  color_left = "#ffffff",
  color_right = "#ffffff",
}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [splitPct, setSplitPct] = useState(isMobile ? 60 : 35);
  const dragging = useRef(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(prev => {
        if (prev !== mobile) setSplitPct(mobile ? 60 : 35);
        return mobile;
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onDragStart = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    document.body.style.cursor = isMobile ? "row-resize" : "col-resize";
    document.body.style.userSelect = "none";
  }, [isMobile]);

  const onDragMove = useCallback((clientX, clientY) => {
    if (!dragging.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = isMobile
      ? ((clientY - rect.top) / rect.height) * 100
      : ((clientX - rect.left) / rect.width) * 100;
    setSplitPct(Math.min(80, Math.max(20, pct)));
  }, [isMobile]);

  const onDragEnd = useCallback(() => {
    dragging.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => onDragMove(e.clientX, e.clientY);
    const handleTouchMove = (e) => {
      const t = e.touches[0];
      onDragMove(t.clientX, t.clientY);
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", onDragEnd);
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchend", onDragEnd);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", onDragEnd);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", onDragEnd);
    };
  }, [onDragMove, onDragEnd]);

  const panelWidth = isMobile
    ? containerRef.current?.getBoundingClientRect().width || window.innerWidth
    : (window.innerWidth * splitPct) / 100;

  return (
    <QuestionStoreProvider>
      <div
        ref={containerRef}
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          width: "100vw",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {/* ── Left / Top panel ── */}
        <div style={{
          [isMobile ? "height" : "width"]: `${splitPct}%`,
          [isMobile ? "width" : "height"]: "100%",
          background: color_left,
          flexShrink: 0,
          overflow: "auto",
          position: "relative",
        }}>
          <LeftPanelContent panelWidth={panelWidth} />
        </div>

        {/* ── Draggable Divider ── */}
        <div
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
          style={{
            [isMobile ? "height" : "width"]: "6px",
            [isMobile ? "width" : "height"]: "100%",
            flexShrink: 0,
            cursor: isMobile ? "row-resize" : "col-resize",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f5f5f5",
            touchAction: "none",
          }}
        >
          <div style={{
            [isMobile ? "width" : "height"]: 32,
            [isMobile ? "height" : "width"]: 4,
            borderRadius: 2,
            background: "#ccc",
          }} />
        </div>

        {/* ── Right / Bottom panel ── */}
        <div style={{
          flex: 1,
          [isMobile ? "width" : "height"]: "100%",
          background: color_right,
          overflow: "auto",
        }}>
          <RightPanel />
        </div>
      </div>
    </QuestionStoreProvider>
  );
}